import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { FiAlertCircle, FiX } from 'react-icons/fi';

// Make sure to call loadStripe outside of a component's render to avoid
// recreating the Stripe object on every render.
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!stripeKey) {
  throw new Error('Missing Stripe publishable key. Please check your environment variables.');
}

const stripePromise = loadStripe(stripeKey);

interface EmbeddedCheckoutModalProps {
  priceId: string;
  userId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function EmbeddedCheckoutModal({ priceId, userId, onSuccess, onCancel }: EmbeddedCheckoutModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchClientSecret = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://devpanc-v2.pages.dev/stripe-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          userId,
          successUrl: `${window.location.origin}/subscribe/success`,
          cancelUrl: `${window.location.origin}/subscribe/cancel`,
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Server response:', response.status, errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.clientSecret) {
        throw new Error('No client secret received from server');
      }

      setClientSecret(data.clientSecret);
      setIsOpen(true);
      
    } catch (error) {
      console.error('Detailed error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [priceId, userId]);

  const handleClose = () => {
    setIsOpen(false);
    setClientSecret(null);
    onCancel?.();
  };

  if (isOpen && clientSecret) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-base-300/80 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div 
          className="w-full max-w-2xl bg-base-100 rounded-lg shadow-xl relative mt-16 sm:mt-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-base-300">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-base-content">
                  {t('payment.checkout.title', 'Secure Checkout')}
                </h2>
                <p className="text-base-content/70 text-sm">
                  {t('payment.checkout.subtitle', 'Complete your purchase securely')}
                </p>
              </div>
              <button 
                onClick={handleClose}
                className="btn btn-ghost btn-sm btn-circle"
                aria-label="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Checkout Form */}
          <div className="p-4 max-h-[70vh] overflow-y-auto">
            <div id="checkout" className="w-full">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret }}
              >
                <EmbeddedCheckout 
                  className="w-full stripe-embedded-checkout" 
                  onComplete={onSuccess}
                />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-base-300">
            <button 
              onClick={handleClose}
              className="btn btn-ghost btn-block"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <button 
        onClick={fetchClientSecret}
        disabled={loading}
        className="mt-8 w-full btn btn-primary"
      >
        {loading ? (
          <>
            <span className="loading loading-spinner"></span>
            {t('common.loading', 'Loading...')}
          </>
        ) : (
          t('payment.checkout.button', 'Proceed to Checkout')
        )}
      </button>
      
      {error && (
        <div className="mt-4 alert alert-error">
          <FiAlertCircle className="w-5 h-5" />
          <span>{t('payment.checkout.error', 'An error occurred during checkout. Please try again.')}</span>
        </div>
      )}
    </div>
  );
}